import React, { lazy, Suspense } from "react";
import { Route, Navigate } from "react-router-dom";
import { withRouter } from "@/helpers/router";
import { LANDSCAPE_DEFAULTS } from "./helpers/defaults";
import { serializeQuery } from "./helpers/url";

const fallback = <div className="page-progress-bar" />;

/** Wraps lazy-loaded components with router compatibility props for legacy class components. */
const loadRoute = (load, options) => {
  const LazyComponent = lazy(load);
  const routeFallback = options?.fallback || fallback;

  return withRouter((props) => (
    <Suspense fallback={routeFallback}>
      <LazyComponent {...props} />
    </Suspense>
  ));
};

const BaseAsync = loadRoute(() => import("./components/base/base"), { fallback });
const HomeAsync = loadRoute(() => import("./components/home/home"), { fallback });
const NotFoundAsync = loadRoute(() => import("./components/base/404.js"), { fallback });

const CaseListAsync = loadRoute(() => import("./components/ptab/case-list/case-list"), { fallback });
const CasesAsync = loadRoute(() => import("./components/ptab/entry"), { fallback });
const AnalyticsAsync = loadRoute(() => import("./components/ptab/analytics/entry"), { fallback });
const PTABAnnualReportAsync = loadRoute(() => import("./components/ptab/annual-report/annual-report"), { fallback });
const CaseAsync = loadRoute(() => import("./components/ptab/detail/entry"), { fallback });
const JudgeAsync = loadRoute(() => import("./components/ptab/judge/detail"), { fallback });

const CaseTabStatusPhaseAsync = loadRoute(() => import("./components/ptab/analytics/case-level/tab-status-phase"), { fallback });
const CaseTabJudgeAsync = loadRoute(() => import("./components/ptab/analytics/case-level/tab-judge"), { fallback });
const CaseTabCounselAsync = loadRoute(() => import("./components/ptab/analytics/case-level/tab-counsel"), { fallback });
const CaseTabSectorAndEntityTypeAsync = loadRoute(() => import("./components/ptab/analytics/case-level/tab-sector-and-entity-type"), { fallback });
const CaseTabDateAsync = loadRoute(() => import("./components/ptab/analytics/case-level/tab-date"), { fallback });
const CaseTabTopPartiesAsync = loadRoute(() => import("./components/ptab/analytics/case-level/tab-top-parties"), { fallback });
const CaseTabISIXAsync = loadRoute(() => import("./components/ptab/analytics/case-level/tab-isix"), { fallback });
const ClaimTabOutcomeAsync = loadRoute(() => import("./components/ptab/analytics/claim-level/tab-outcome"), { fallback });
const ClaimTabJudgeAsync = loadRoute(() => import("./components/ptab/analytics/claim-level/tab-judge"), { fallback });
const ClaimTabCounselAsync = loadRoute(() => import("./components/ptab/analytics/claim-level/tab-counsel"), { fallback });

const PatentDetailAsync = loadRoute(() => import("./components/patent/detail/entry"), { fallback });
const PatentSearchAsync = loadRoute(() => import("./components/patent/entry"), { fallback });
const PatentListAsync = loadRoute(() => import("./components/patent/table"), { fallback });
const PatentAnalyticsAsync = loadRoute(() => import("./components/patent/analytics/entry"), { fallback });
const PatentTopAssigneeAsync = loadRoute(() => import("./components/patent/analytics/top-assignee"), { fallback });
const PatentTopLawfirmAsync = loadRoute(() => import("./components/patent/analytics/top-lawfirm"), { fallback });
const PatentOverYearAsync = loadRoute(() => import("./components/patent/analytics/over-year"), { fallback });
const PatentOppositionAsync = loadRoute(() => import("./components/patent/analytics/opposition"), { fallback });
const PatentRelatedCasesAnalyticsAsync = loadRoute(() => import("./components/patent/analytics/ptab-and-litigation"), { fallback });

const ProsecutionAsync = loadRoute(() => import("./components/prosecution/prosecution"), { fallback });
const ProsecutionEntityAsync = loadRoute(() => import("./components/prosecution/entity-detail"), { fallback });

const Projects = loadRoute(() => import("./components/portfolio/projects"), { fallback });
const Portfolios = loadRoute(() => import("./components/portfolio/portfolios"), { fallback });
const PortfolioBBExample = loadRoute(() => import("./components/portfolio/blackberry-example"), { fallback });
const PriorArtAsync = loadRoute(() => import("./components/prior-art/prior-art"), { fallback });

const ClaimChartContextWrapper = loadRoute(() => import("./components/claim-chart/claim-chart-context-wrapper.js"), { fallback });
const ClaimChart = loadRoute(() => import("./components/claim-chart/claim-chart"), { fallback });
const ClaimChartProjects = loadRoute(() => import("./components/claim-chart/claim-chart-projects.js"), { fallback });

const HEVCLandscapeAsync = loadRoute(() => import("./components/enterprise/landscape-hevc"), { fallback });
const AVCLandscapeAsync = loadRoute(() => import("./components/enterprise/landscape-avc"), { fallback });
const LTELandscapeAsync = loadRoute(() => import("./components/enterprise/landscape-lte"), { fallback });
const LTERANLandscapeAsync = loadRoute(() => import("./components/enterprise/landscape-lte-ran"), { fallback });
const FiveGLandscapeAsync = loadRoute(() => import("./components/enterprise/landscape-5g"), { fallback });
const FiveGRANLandscapeAsync = loadRoute(() => import("./components/enterprise/landscape-5g-ran"), { fallback });
const WIFI6LandscapeAsync = loadRoute(() => import("./components/enterprise/landscape-wifi6"), { fallback });
const VVCLandscapeAsync = loadRoute(() => import("./components/enterprise/landscape-vvc"), { fallback });
const GenAILandscapeAsync = loadRoute(() => import("./components/enterprise/landscape-genai"), { fallback });
const HEVCRepoAsync = loadRoute(() => import("./components/enterprise/repo-hevc"), { fallback });
const Repo3GPPAsync = loadRoute(() => import("./components/enterprise/repo-3gpp"), { fallback });
const RepoIeeeAsync = loadRoute(() => import("./components/enterprise/repo-ieee"), { fallback });
const RepoIetfAsync = loadRoute(() => import("./components/enterprise/repo-ietf"), { fallback });

const LitigationAsync = loadRoute(() => import("./components/litigation/detail/entry"), { fallback });
const LitigationSearchAsync = loadRoute(() => import("./components/litigation/entry"), { fallback });
const LitigationCaseListAsync = loadRoute(() => import("./components/litigation/table"), { fallback });
const LitigationAnalyticsAsync = loadRoute(() => import("./components/litigation/analytics/entry"), { fallback });
const LitigationAnnualReportAsync = loadRoute(() => import("./components/litigation/annual-report"), { fallback });

const ReexamSearchAsync = loadRoute(() => import("./components/re-exam/entry"), { fallback });
const ReexamListAsync = loadRoute(() => import("./components/re-exam/table"), { fallback });
const ReexamDetailAsync = loadRoute(() => import("./components/re-exam/detail/entry"), { fallback });
const ReexamAnalyticsAsync = loadRoute(() => import("./components/re-exam/analytics/entry"), { fallback });

const InternationalSearchAsync = loadRoute(() => import("./components/international/entry"), { fallback });
const InternationalListAsync = loadRoute(() => import("./components/international/table"), { fallback });
const InternationalAnalyticsAsync = loadRoute(() => import("./components/international/analytics/entry"), { fallback });

const UPCSearchAsync = loadRoute(() => import("./components/upc/entry"), { fallback });
const UPCListAsync = loadRoute(() => import("./components/upc/table"), { fallback });
const UPCAnalyticsAsync = loadRoute(() => import("./components/upc/analytics/entry"), { fallback });
const UPCDetailAsync = loadRoute(() => import("./components/upc/detail/entry"), { fallback });

const CompanyAsync = loadRoute(() => import("./components/company/detail"), { fallback });

const AppRoutes = (
  <>
    <Route element={<BaseAsync />}>
      <Route element={<HomeAsync />} path="/" />
      <Route element={<CasesAsync />}>
        <Route element={<CaseListAsync />} path="/ptab/caselist" />
        <Route element={<AnalyticsAsync />}>
          <Route element={<CaseTabDateAsync />} path="/ptab/analytics/case-level/by-date" />
          <Route element={<CaseTabTopPartiesAsync />} path="/ptab/analytics/case-level/top-parties" />
          <Route element={<CaseTabStatusPhaseAsync />} path="/ptab/analytics/case-level/by-status-and-phase" />
          <Route element={<CaseTabSectorAndEntityTypeAsync />} path="/ptab/analytics/case-level/by-sector-and-entity-type" />
          <Route element={<CaseTabISIXAsync />} path="/ptab/analytics/case-level/isix" />
          <Route element={<CaseTabJudgeAsync />} path="/ptab/analytics/case-level/by-judge" />
          <Route element={<CaseTabCounselAsync />} path="/ptab/analytics/case-level/by-counsel" />
          <Route element={<ClaimTabOutcomeAsync />} path="/ptab/analytics/claim-level/by-outcome" />
          <Route element={<ClaimTabJudgeAsync />} path="/ptab/analytics/claim-level/by-judge" />
          <Route element={<ClaimTabCounselAsync />} path="/ptab/analytics/claim-level/by-counsel" />
        </Route>
      </Route>
      <Route element={<PTABAnnualReportAsync />} path="/ptab/annual-report" />
      <Route element={<CaseAsync />} path="/ptab/case/:casenum" />
      <Route element={<JudgeAsync />} path="/ptab/judge/:name" />
      <Route element={<PatentSearchAsync />}>
        <Route element={<PatentListAsync />} path="/patents/search" />
        <Route element={<PatentAnalyticsAsync />}>
          <Route element={<Navigate to="/patents/analytics/top-assignees" replace={true} />} path="/patents/analytics" />
          <Route element={<PatentTopAssigneeAsync />} path="/patents/analytics/top-assignees" />
          <Route element={<PatentTopLawfirmAsync />} path="/patents/analytics/top-law-firms" />
          <Route element={<PatentRelatedCasesAnalyticsAsync />} path="/patents/analytics/ptab-and-litigation" />
          <Route element={<PatentOverYearAsync />} path="/patents/analytics/by-date" />
          <Route element={<PatentOppositionAsync />} path="/patents/analytics/opposition" />
        </Route>
      </Route>
      <Route element={<PatentDetailAsync />} path="/patents/patent/:patentnum" />
      <Route element={<ReexamSearchAsync />}>
        <Route element={<ReexamListAsync />} path="/exparte/search" />
        <Route element={<ReexamAnalyticsAsync />} path="/exparte/analytics" />
      </Route>
      <Route element={<ReexamDetailAsync />} path="/exparte/:appnum" />
      <Route element={<ProsecutionAsync />} path="/prosecution" />
      <Route element={<ProsecutionEntityAsync />} path="/prosecution/examiner/:name" />
      <Route element={<ProsecutionEntityAsync />} path="/prosecution/lawfirm/:name" />
      <Route element={<ProsecutionEntityAsync />} path="/prosecution/artunit/:name" />
      <Route element={<Navigate to="/prosecution" replace={true} />} path="/patents/examiner" />
      <Route element={<PriorArtAsync />} path="/patents/prior-art/:activetab" />
      <Route element={<Navigate to="/patents/prior-art/pqai" replace={true} />} path="/patents/prior-art" />
      <Route element={<Projects />}>
        <Route element={<Portfolios />} path="/patents/portfolio" />
        <Route element={<Portfolios />} path="/patents/portfolio/my/:project" />
        <Route element={<PortfolioBBExample />} path="/patents/portfolio/up/bb-example" />
      </Route>
      <Route element={<ClaimChartContextWrapper />}>
        <Route element={<ClaimChartProjects />}>
          <Route element={<ClaimChart />} path="/patents/invalidity" />
          <Route element={<ClaimChart />} path="/patents/invalidity/my/:chartId" />
          <Route element={<ClaimChart />} path="/patents/infringement" />
          <Route element={<ClaimChart />} path="/patents/infringement/my/:chartId" />
          <Route element={<ClaimChart />} path="/patents/manual" />
          <Route element={<ClaimChart />} path="/patents/manual/my/:chartId" />
        </Route>
      </Route>
      <Route element={<HEVCLandscapeAsync />} path="/standards/opal/hevc/:version" />
      <Route element={<Navigate to={`/standards/opal/hevc/${LANDSCAPE_DEFAULTS["hevc"].version}${serializeQuery(LANDSCAPE_DEFAULTS["hevc"].filters[LANDSCAPE_DEFAULTS["hevc"].version])}`} replace={true} />} path="/standards/opal/hevc" />
      <Route element={<AVCLandscapeAsync />} path="/standards/opal/avc/:version" />
      <Route element={<Navigate to={`/standards/opal/avc/${LANDSCAPE_DEFAULTS["avc"].version}${serializeQuery(LANDSCAPE_DEFAULTS["avc"].filters[LANDSCAPE_DEFAULTS["avc"].version])}`} replace={true} />} path="/standards/opal/avc" />
      <Route element={<LTELandscapeAsync />} path="/standards/opal/lte/:version" />
      <Route element={<Navigate to={`/standards/opal/lte/${LANDSCAPE_DEFAULTS["lte"].version}${serializeQuery(LANDSCAPE_DEFAULTS["lte"].filters[LANDSCAPE_DEFAULTS["lte"].version])}`} replace={true} />} path="/standards/opal/lte" />
      <Route element={<LTERANLandscapeAsync />} path="/standards/opal/lte_ran/:version" />
      <Route element={<Navigate to={`/standards/opal/lte_ran/${LANDSCAPE_DEFAULTS["lte_ran"].version}${serializeQuery(LANDSCAPE_DEFAULTS["lte_ran"].filters[LANDSCAPE_DEFAULTS["lte_ran"].version])}`} replace={true} />} path="/standards/opal/lte_ran" />
      <Route element={<FiveGLandscapeAsync />} path="/standards/opal/5g/:version" />
      <Route element={<Navigate to={`/standards/opal/5g/${LANDSCAPE_DEFAULTS["5g"].version}${serializeQuery(LANDSCAPE_DEFAULTS["5g"].filters[LANDSCAPE_DEFAULTS["5g"].version])}`} replace={true} />} path="/standards/opal/5g" />
      <Route element={<FiveGRANLandscapeAsync />} path="/standards/opal/5g_ran/:version" />
      <Route element={<Navigate to={`/standards/opal/5g_ran/${LANDSCAPE_DEFAULTS["5g_ran"].version}${serializeQuery(LANDSCAPE_DEFAULTS["5g_ran"].filters[LANDSCAPE_DEFAULTS["5g_ran"].version])}`} replace={true} />} path="/standards/opal/5g_ran" />
      <Route element={<WIFI6LandscapeAsync />} path="/standards/opal/wifi6/:version" />
      <Route element={<Navigate to={`/standards/opal/wifi6/${LANDSCAPE_DEFAULTS["wifi6"].version}${serializeQuery(LANDSCAPE_DEFAULTS["wifi6"].filters[LANDSCAPE_DEFAULTS["wifi6"].version])}`} replace={true} />} path="/standards/opal/wifi6" />
      <Route element={<VVCLandscapeAsync />} path="/standards/opal/vvc/:version" />
      <Route element={<Navigate to={`/standards/opal/vvc/${LANDSCAPE_DEFAULTS["vvc"].version}${serializeQuery(LANDSCAPE_DEFAULTS["vvc"].filters[LANDSCAPE_DEFAULTS["vvc"].version])}`} replace={true} />} path="/standards/opal/vvc" />
      <Route element={<GenAILandscapeAsync />} path="/standards/opal/genai/:version" />
      <Route element={<Navigate to={`/standards/opal/genai/${LANDSCAPE_DEFAULTS["genai"].version}${serializeQuery(LANDSCAPE_DEFAULTS["genai"].filters[LANDSCAPE_DEFAULTS["genai"].version])}`} replace={true} />} path="/standards/opal/genai" />
      <Route element={<HEVCRepoAsync />} path="/standards/open/mpeg" />
      <Route element={<Navigate to="/standards/open/mpeg" replace={true} />} path="/standards/assure/hevc" />
      <Route element={<Repo3GPPAsync />} path="/standards/open/3gpp" />
      <Route element={<RepoIeeeAsync />} path="/standards/open/ieee" />
      <Route element={<RepoIetfAsync />} path="/standards/open/ietf" />
      <Route element={<LitigationSearchAsync />}>
        <Route element={<LitigationCaseListAsync />} path="/litigation/caselist" />
        <Route element={<LitigationAnalyticsAsync />} path="/litigation/analytics" />
      </Route>
      <Route element={<LitigationAsync />} path="/litigation/:courtname/case/:casenum" />
      <Route element={<Navigate to="/litigation/:courtname/case/:casenum" replace={true} />} path="/litigation/profile/:casenum/:courtname" />
      <Route element={<LitigationAnnualReportAsync />} path="/litigation/annual-report" />
      <Route element={<InternationalSearchAsync />}>
        <Route element={<InternationalListAsync />} path="/international-challenges/search" />
        <Route element={<InternationalAnalyticsAsync />} path="/international-challenges/analytics" />
      </Route>
      <Route element={<UPCSearchAsync />}>
        <Route element={<UPCListAsync />} path="/upc/search" />
        <Route element={<UPCAnalyticsAsync />} path="/upc/analytics" />
      </Route>
      <Route element={<UPCDetailAsync />} path="/upc/case/:casenum" />
      <Route element={<CompanyAsync />} path="/company/:name" />
      <Route element={<NotFoundAsync />} path="*" />
    </Route>
  </>
);
export default AppRoutes;
